import { updateReservablesConfig } from '@go-client/lib/api/user';
import { notUndefined } from '@go-shared/lib/functional';
import { User, UserReservablesConfig } from '@go-shared/types/user';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { BubbleButton, BubbleCheckbox, BubbleFormGroup, BubbleImageSelector } from '../BubbleUI';
import StripeOnboarding from '../StripeOnboarding';

interface HostProfileFormDataBase {
  config: UserReservablesConfig;
}

type HostProfileFormData = HostProfileFormDataBase & {
  _dirty: boolean;
  _errors: Partial<HostProfileFormErrors>;
};

type HostProfileFormErrors = { [K in keyof HostProfileFormDataBase]: string };

function DEFAULT_FORM_DATA(user?: User): HostProfileFormData {
  return {
    config: { ...(user?.reservablesConfig ?? {}) },
    _dirty: false,
    _errors: {},
  };
}

interface HostProfileFormProps {
  edit: User;
  actionsElem?: HTMLDivElement;
  onFormSubmitted?: () => Promise<void>;
}

export default function HostProfileForm(props: HostProfileFormProps) {
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState<HostProfileFormData>(DEFAULT_FORM_DATA(props.edit));
  const hasAcceptedTerms = !!props.edit.reservablesConfig?.hasAgreedToHostTerms;

  useEffect(() => {
    resetForm();
  }, [props.edit]);

  function resetForm() {
    setFormData(DEFAULT_FORM_DATA(props.edit));
  }

  function changeForm<T extends keyof UserReservablesConfig>(
    key: T,
    val: UserReservablesConfig[T]
  ) {
    setFormData({ ...formData, _dirty: true, config: { ...formData.config, [key]: val } });
  }

  async function submitForm() {
    setSaving(true);

    try {
      await updateReservablesConfig({
        ...(props.edit.reservablesConfig || {}),
        ...formData.config,
      });

      await props.onFormSubmitted?.();
    } finally {
      setSaving(false);
    }
  }

  const fullFormHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Avatar" className="col-span-3">
        <BubbleImageSelector
          maxItems={1}
          value={[formData.config.avatar].filter(notUndefined)}
          onValue={images => changeForm('avatar', images[0])}
        />
      </BubbleFormGroup>
      <BubbleFormGroup label="Stripe Account" className="col-span-3">
        <StripeOnboarding
          identifier={props.edit._id}
          mode="host"
          stripeAccountId={props.edit.reservablesConfig?.stripeAccountId}
          hasCompletedOnboarding={!!props.edit.reservablesConfig?.hasCompletedOnboarding}
        />
      </BubbleFormGroup>
    </div>
  );

  const termsFormHtml = (
    <div className="grid grid-cols-3 gap-4">
      <BubbleFormGroup label="Host Terms of Service" className="col-span-3">
        <fieldset className="bg-white p-4 border rounded shadow grid grid-cols-1 gap-4 justify-items-start">
          <div>
            Before editing your profile icon or onboarding with Stripe,{' '}
            <span className="font-semibold">you must review and accept</span> the host terms.
          </div>
          <BubbleButton variant="navy" inverse className="pl-0-i relative" icon="arrow-up-right">
            <a
              href="https://drive.google.com/file/d/14DVKcfbYQJZARxTIYf-Anui_r2GMuvVc/view?usp=sharing"
              className="stretched-link"
              target="_blank"
              rel="noopener noreferrer"
            >
              Click HERE to open the Host Terms of Service (PDF)
            </a>
          </BubbleButton>
          <BubbleCheckbox
            checked={!!formData.config.hasAgreedToHostTerms}
            onValue={val => changeForm('hasAgreedToHostTerms', !!val)}
            label="I have read and agree to the Host Terms of Service"
          />
        </fieldset>
      </BubbleFormGroup>
    </div>
  );

  const formHtml = hasAcceptedTerms ? fullFormHtml : termsFormHtml;

  const formActions = (
    <div className="w-full flex items-center">
      <div className="ml-auto" />
      <BubbleButton
        variant="navy"
        onClick={resetForm}
        inverse
        disabled={!formData._dirty || saving}
        icon="rotate-left"
      >
        Reset
      </BubbleButton>
      <BubbleButton
        variant="navy"
        onClick={submitForm}
        disabled={!formData._dirty || saving}
        icon="floppy-disk"
      >
        Save Changes
      </BubbleButton>
    </div>
  );

  const portalButtons = props.actionsElem ? createPortal(formActions, props.actionsElem) : <></>;

  return (
    <>
      {formHtml}
      {portalButtons}
    </>
  );
}
